import { create } from 'zustand';
import type { Camera } from 'three';
import { Sensor, Obstacle, Project, ReferenceImageConfig, AreaOfInterest, TargetConfig, GridParams, SensorParameters, AlignmentStatus } from '../api/models';
import { startHeatmapAnalysis, cancelHeatmapAnalysis, fetchAlignmentConnectivity, autoFloorSensor as autoFloorSensorApi, runIcpAlignment as runIcpAlignmentApi } from '../api/client';
import { computeAlignmentConnectivityLocal, nextAlignmentCandidateId } from '../utils/alignmentConnectivity';
import {
  ImageDragTransform,
  scaleObstaclesWithImageDrag,
  scaleSensorsWithImageDrag,
} from '../utils/referenceImageSceneScale';
import type { AlignmentConnectivityResponse } from '../api/models';

import { v4 as uuidv4 } from 'uuid';

export type ColorScale = 'blue-red' | 'viridis' | 'plasma' | 'inferno' | 'magma' | 'turbo' | 'jet';

export interface SensorPoseSnapshot {
  sensorId: string;
  position: [number, number, number];
  rotation: [number, number, number];
  floorLocked?: boolean;
  alignmentStatus?: AlignmentStatus;
}
export type PointRenderMode = 'fast' | 'robust' | 'auto';

const MAX_SENSOR_POSE_HISTORY = 50;

let alignmentConnectivityRequestSeq = 0;

const snapshotSensorPose = (sensor: Sensor): SensorPoseSnapshot => ({
  sensorId: sensor.id,
  position: [...sensor.position] as [number, number, number],
  rotation: [...sensor.rotation] as [number, number, number],
  floorLocked: sensor.floorLocked,
  alignmentStatus: sensor.alignmentStatus,
});

const applyPoseSnapshot = (sensors: Sensor[], snapshot: SensorPoseSnapshot): Sensor[] =>
  sensors.map((sensor) => {
    if (sensor.id !== snapshot.sensorId) {
      return sensor;
    }
    return {
      ...sensor,
      position: [...snapshot.position] as [number, number, number],
      rotation: [...snapshot.rotation] as [number, number, number],
      floorLocked: snapshot.floorLocked,
      alignmentStatus: snapshot.alignmentStatus,
    };
  });

const sameVector3 = (
  a?: [number, number, number],
  b?: [number, number, number]
) => Boolean(a && b && a[0] === b[0] && a[1] === b[1] && a[2] === b[2]);

const sensorPoseChanged = (before: Sensor, after: Sensor): boolean =>
  !sameVector3(before.position, after.position)
  || !sameVector3(before.rotation, after.rotation);

const sensorSimulationInputsChanged = (before: Sensor, after: Sensor): boolean =>
  sensorPoseChanged(before, after)
  || before.type !== after.type
  || before.rotationRate !== after.rotationRate
  || JSON.stringify(before.parameters) !== JSON.stringify(after.parameters);

const alignmentInvalidationForPoseProps = (
  sensor: Sensor,
  props: Partial<Sensor>,
): Partial<Sensor> => {
  const positionChanged = props.position !== undefined
    && !sameVector3(props.position, sensor.position);
  const rotationChanged = props.rotation !== undefined
    && !sameVector3(props.rotation, sensor.rotation);
  if (
    sensor.alignmentStatus === 'aligned'
    && props.alignmentStatus === undefined
    && (positionChanged || rotationChanged)
  ) {
    return { alignmentStatus: 'unaligned' };
  }
  return {};
};

const simulationInvalidationState = (): Pick<SceneState, 'shouldSimulate' | 'heatmapData' | 'heatmapVisible'> => ({
  shouldSimulate: true,
  heatmapData: null,
  heatmapVisible: false,
});

const cancelHeatmapTaskIfRunning = (get: () => SceneState) => {
  const state = get();
  if (state.heatmapTaskId) {
    void state.cancelHeatmapGeneration();
  }
};

export interface SceneOptions {
  gridCellSize: number;
  heatmapScale: {
    min: number;
    max: number;
  };
  areaOfInterest: AreaOfInterest;
  resolution: number;
  pointSize: number;
  pointRenderMode: PointRenderMode;
  colorScale: ColorScale;
  targetConfig: TargetConfig;
  cameraMode: '3D' | '2D';
}

export interface SceneState {
  sensors: Sensor[];
  obstacles: Obstacle[];
  isDragging: boolean;
  setIsDragging: (isDragging: boolean) => void;
  transformMode: 'translate' | 'rotate';
  setTransformMode: (mode: 'translate' | 'rotate') => void;
  shouldSimulate: boolean;
  setShouldSimulate: (should: boolean) => void;
  heatmapData: number[][] | null;
  setHeatmapData: (data: number[][] | null) => void;
  heatmapTaskId: string | null;
  setHeatmapTaskId: (id: string | null) => void;
  startHeatmapGeneration: (targetParams: { width: number; height: number; depth: number; shape: string; ground_offset: number }, gridParams: GridParams) => Promise<void>;
  cancelHeatmapGeneration: () => Promise<void>;
  heatmapVisible: boolean;
  setHeatmapVisible: (visible: boolean) => void;

  referenceImageVisible: boolean;
  setReferenceImageVisible: (visible: boolean) => void;
  pointCloudVisible: boolean;
  setPointCloudVisible: (visible: boolean) => void;
  preAlignmentPointCloudVisible: boolean | null;
  alignmentPointCloudUserToggled: boolean;
  /** Cloud ids are namespaced as `${scope}:${sensorId}` so design and align track independently. */
  cloudLoadPendingIds: string[];
  cloudLoadReadyIds: string[];
  designCloudLoadError: string | null;
  designCloudReloadNonce: number;
  resetCloudLoading: () => void;
  clearCloudLoadingScope: (scope: string) => void;
  registerCloudPending: (cloudId: string) => void;
  registerCloudReady: (cloudId: string) => void;
  unregisterCloud: (cloudId: string) => void;
  setDesignCloudLoadError: (error: string | null) => void;
  requestDesignCloudReload: () => void;
  obstaclesVisible: boolean;
  setObstaclesVisible: (visible: boolean) => void;
  isReadOnly: boolean;
  setIsReadOnly: (isReadOnly: boolean) => void;
  currentProject: Project | null;
  updateCurrentProject: (project: Project | null) => void;
  updateProjectReferenceImage: (imageUrl: string, originalFilename?: string) => void;
  referenceImageConfig: ReferenceImageConfig;
  setReferenceImageConfig: (config: Partial<ReferenceImageConfig>) => void;
  resetReferenceImageConfig: () => void;
  sceneOptions: SceneOptions;
  setSceneOptions: (options: Partial<SceneOptions> | ((opts: SceneOptions) => Partial<SceneOptions>)) => void;
  selectedObjectId: string | null;
  setSelectedObjectId: (id: string | null) => void;
  isReferenceImageAlignMode: boolean;
  setIsReferenceImageAlignMode: (isMode: boolean) => void;
  referenceImageAlignMethod: 'interactive' | 'point-picking';
  setReferenceImageAlignMethod: (method: 'interactive' | 'point-picking') => void;
  referenceImageAlignMode: 'translate' | 'rotate' | 'scale' | null;
  setReferenceImageAlignMode: (mode: 'translate' | 'rotate' | 'scale' | null) => void;
  referenceImageScaleSensors: boolean;
  setReferenceImageScaleSensors: (scale: boolean) => void;
  referenceImageScaleObstacles: boolean;
  setReferenceImageScaleObstacles: (scale: boolean) => void;
  applyReferenceImageScaleToScene: (
    baselineSensors: Sensor[],
    baselineObstacles: Obstacle[],
    pivotStart: [number, number],
    pivotEnd: [number, number],
    scaleFactor: number,
    options: { scaleSensors: boolean; scaleObstacles: boolean },
  ) => void;
  pointPickingStep: 'origin' | 'xaxis' | 'scale-1' | 'scale-2' | 'scale-distance' | null;
  setPointPickingStep: (step: 'origin' | 'xaxis' | 'scale-1' | 'scale-2' | 'scale-distance' | null) => void;
  pickingState: {
    origin: [number, number] | null;
    xAxisEnd: [number, number] | null;
    scalePointA: [number, number] | null;
    scalePointB: [number, number] | null;
  };
  setPickingState: (state: Partial<SceneState['pickingState']>) => void;
  selectedSensorType: string;
  setSelectedSensorType: (type: string) => void;
  useOrthographic: boolean;
  setUseOrthographic: (use: boolean) => void;
  orthographicZoom: number | null;
  setOrthographicZoom: (zoom: number) => void;
  defaultSensorHeight: number;
  defaultSensorRotationRate: number;
  defaultSensorParameters: SensorParameters;
  setDefaultSensorHeight: (height: number) => void;
  setDefaultSensorRotationRate: (rate: number) => void;
  setDefaultSensorParameters: (params: Partial<SensorParameters>) => void;
  lastAddedSensorId: string | null;
  setLastAddedSensorId: (id: string | null) => void;
  updateSensor: (id: string, props: Partial<Sensor>) => void;
  updateObstacleProperties: (id: string, props: Partial<Obstacle>) => void;
  addObstacle: (position?: [number, number, number]) => void;
  addSensor: (type: string, position2d?: [number, number]) => void;
  deleteSensor: (id: string) => void;
  deleteObstacle: (id: string) => void;
  // Wall drawing state
  isWallDrawingMode: boolean;
  setIsWallDrawingMode: (isWallDrawingMode: boolean) => void;
  // Sensor alignment state
  isAlignmentMode: boolean;
  setIsAlignmentMode: (isAlignmentMode: boolean) => void;
  commitSensorAlignment: (id: string) => void;
  resetSensorToDesign: (id: string) => void;
  restoreSensorToAligned: (id: string) => void;
  alignmentOriginSensorId: string | null;
  alignmentConnectivity: AlignmentConnectivityResponse | null;
  alignmentNavigationHistory: string[];
  advanceAlignmentSensor: (direction: 'next' | 'prev') => void;
  autoFloorSensor: (id: string) => Promise<{ success: boolean; message: string }>;
  runIcpSensor: (id: string) => Promise<{ success: boolean; message: string }>;
  sensorPoseHistory: SensorPoseSnapshot[];
  sensorPoseRedoHistory: SensorPoseSnapshot[];
  pushSensorPoseHistory: (sensorId: string) => void;
  undoSensorPose: () => void;
  redoSensorPose: () => void;
  alignmentHeightMin: number | null;
  alignmentHeightMax: number | null;
  setAlignmentHeightMin: (value: number | null) => void;
  setAlignmentHeightMax: (value: number | null) => void;
  refreshAlignmentConnectivity: (selectRecommendedNext?: boolean) => Promise<void>;
  currentWallPoints: [number, number][];
  setCurrentWallPoints: (points: [number, number][]) => void;
  wallThickness: number;
  setWallThickness: (thickness: number) => void;
  wallHeight: number;
  setWallHeight: (height: number) => void;
  finishCurrentWall: () => void;
  clearCurrentWall: () => void;
  checkAndLoadCachedHeatmap: (projectId: number) => Promise<boolean>;
  // Transient refs to the WebGL canvas and active camera so non-R3F components
  // (e.g. SimulationPanel) can capture the rendered scene for image export.
  sceneCanvas: HTMLCanvasElement | null;
  sceneCamera: Camera | null;
  setSceneRefs: (canvas: HTMLCanvasElement | null, camera: Camera | null) => void;
}

const initialReferenceImageConfig: ReferenceImageConfig = {
  origin: null,
  xAxisEnd: null,
  scalePointA: null,
  scalePointB: null,
  scaleDistance: null,
};

export const useSceneStore = create<SceneState>((set, get) => ({
  sensors: [],
  obstacles: [],
  isDragging: false,
  setIsDragging: (isDragging: boolean) => set({ isDragging }),
  transformMode: 'translate',
  setTransformMode: (mode: 'translate' | 'rotate') => set({ transformMode: mode }),
  shouldSimulate: true,
  setShouldSimulate: (should: boolean) => set({ shouldSimulate: should }),
  heatmapData: null,
  setHeatmapData: (data: number[][] | null) => set({ heatmapData: data }),
  heatmapTaskId: null,
  setHeatmapTaskId: (id: string | null) => set({ heatmapTaskId: id }),
  startHeatmapGeneration: async (targetParams, gridParams) => {
    // Determine sensors and obstacles from current state
    const currentState = get();
    const scene = {
      sensors: currentState.sensors,
      obstacles: currentState.obstacles,
    };

    try {
      const task = await startHeatmapAnalysis(scene, targetParams, gridParams);
      set({ heatmapTaskId: task.task_id, heatmapData: null, heatmapVisible: false });
    } catch (error) {
      console.error('Failed to start heatmap generation:', error);
      throw error;
    }
  },
  cancelHeatmapGeneration: async () => {
    const currentState = get();
    const taskId = currentState.heatmapTaskId;
    if (taskId) {
      set({ heatmapTaskId: null });
      try {
        await cancelHeatmapAnalysis(taskId);
      } catch (error) {
        console.error('Error cancelling heatmap analysis:', error);
      }
    }
  },
  heatmapVisible: false,
  setHeatmapVisible: (visible: boolean) => set({ heatmapVisible: visible }),

  referenceImageVisible: true,
  setReferenceImageVisible: (visible: boolean) => set({ referenceImageVisible: visible }),
  pointCloudVisible: true,
  setPointCloudVisible: (visible: boolean) => set((state) => {
    const updates: Partial<SceneState> = { pointCloudVisible: visible };
    if (state.isAlignmentMode && state.preAlignmentPointCloudVisible !== null) {
      updates.alignmentPointCloudUserToggled = true;
    }
    return updates;
  }),
  preAlignmentPointCloudVisible: null,
  alignmentPointCloudUserToggled: false,
  cloudLoadPendingIds: [],
  cloudLoadReadyIds: [],
  designCloudLoadError: null,
  designCloudReloadNonce: 0,
  resetCloudLoading: () => set({
    cloudLoadPendingIds: [],
    cloudLoadReadyIds: [],
    designCloudLoadError: null,
  }),
  clearCloudLoadingScope: (scope: string) =>
    set((state) => {
      const prefix = `${scope}:`;
      return {
        cloudLoadPendingIds: state.cloudLoadPendingIds.filter((id) => !id.startsWith(prefix)),
        cloudLoadReadyIds: state.cloudLoadReadyIds.filter((id) => !id.startsWith(prefix)),
        ...(scope === 'design' ? { designCloudLoadError: null } : {}),
      };
    }),
  registerCloudPending: (cloudId: string) =>
    set((state) => {
      if (
        state.cloudLoadPendingIds.includes(cloudId)
        || state.cloudLoadReadyIds.includes(cloudId)
      ) {
        return state;
      }
      return {
        cloudLoadPendingIds: [...state.cloudLoadPendingIds, cloudId],
      };
    }),
  registerCloudReady: (cloudId: string) =>
    set((state) => ({
      cloudLoadPendingIds: state.cloudLoadPendingIds.filter((id) => id !== cloudId),
      cloudLoadReadyIds: state.cloudLoadReadyIds.includes(cloudId)
        ? state.cloudLoadReadyIds
        : [...state.cloudLoadReadyIds, cloudId],
    })),
  unregisterCloud: (cloudId: string) =>
    set((state) => ({
      cloudLoadPendingIds: state.cloudLoadPendingIds.filter((id) => id !== cloudId),
      cloudLoadReadyIds: state.cloudLoadReadyIds.filter((id) => id !== cloudId),
    })),
  setDesignCloudLoadError: (error: string | null) => set({ designCloudLoadError: error }),
  requestDesignCloudReload: () =>
    set((state) => ({
      designCloudLoadError: null,
      designCloudReloadNonce: state.designCloudReloadNonce + 1,
    })),
  obstaclesVisible: true,
  setObstaclesVisible: (visible: boolean) => set({ obstaclesVisible: visible }),
  isReadOnly: false,
  setIsReadOnly: (isReadOnly: boolean) => set({ isReadOnly, selectedObjectId: null }),
  currentProject: null,
  checkAndLoadCachedHeatmap: async (projectId: number) => {
    console.log('checkAndLoadCachedHeatmap called for project id:', projectId);
    const isReadOnly = get().isReadOnly;

    // In demo/read-only mode, attempt to load static heatmap
    if (isReadOnly) {
      console.log('Demo mode detected: Loading static heatmap...');
      try {
        const response = await fetch('/demo_heatmap.json');
        if (response.ok) {
          const data = await response.json();
          set({
            heatmapData: data,
            heatmapVisible: true,
          });
          return true;
        }
      } catch (error) {
        console.error('Error loading demo heatmap:', error);
      }
      return false;
    }

    try {
      const { checkHeatmapCache, loadCachedHeatmap } = await import('../api/client');
      console.log('Calling checkHeatmapCache...');
      const cacheResult = await checkHeatmapCache(projectId);
      console.log('checkHeatmapCache result:', cacheResult);

      if (cacheResult.has_cache) {
        console.log('Cache found, loading...');
        const cachedData = await loadCachedHeatmap(projectId, cacheResult.cache_key);
        console.log('Cache loaded, setting heatmap data');
        set({
          heatmapData: cachedData.heatmap,
          heatmapVisible: true,
        });
        return true; // Cache loaded successfully
      }
      console.log('No cache available');
      return false; // No cache available
    } catch (error) {
      console.error('Failed to check/load cached heatmap:', error);
      return false;
    }
  },
  updateCurrentProject: (project: Project | null) => {
    // Persist project ID
    if (project?.id) {
      localStorage.setItem('lastProjectId', project.id.toString());
    } else {
      localStorage.removeItem('lastProjectId');
    }

    // Cancel any ongoing heatmap generation when switching projects
    const currentState = get();
    if (currentState.heatmapTaskId) {
      currentState.cancelHeatmapGeneration();
    }

    const newSceneOptions: SceneOptions = project?.simulation_options ? {
      // Ensure all required fields are present with defaults if missing
      gridCellSize: project.simulation_options.gridCellSize ?? 1,
      heatmapScale: {
        min: project.simulation_options.heatmapScale?.min ?? 0,
        max: project.simulation_options.heatmapScale?.max ?? 50,
      },
      areaOfInterest: {
        min_x: project.simulation_options.areaOfInterest?.min_x ?? -100,
        max_x: project.simulation_options.areaOfInterest?.max_x ?? 100,
        min_y: project.simulation_options.areaOfInterest?.min_y ?? -100,
        max_y: project.simulation_options.areaOfInterest?.max_y ?? 100,
      },
      resolution: project.simulation_options.resolution ?? 1,
      pointSize: project.simulation_options.pointSize ?? 3,
      pointRenderMode: (project.simulation_options.pointRenderMode ?? 'robust') as PointRenderMode,
      colorScale: (project.simulation_options.colorScale ?? 'jet') as ColorScale,
      targetConfig: project.simulation_options.targetConfig ?? {
        width: 1.0,
        height: 1.8,
        depth: 0.5,
        shape: 'cuboid',
        groundOffset: 0.1
      },
      cameraMode: (project.simulation_options?.cameraMode as '3D' | '2D') ?? '3D',
    } : {
      gridCellSize: 1,
      heatmapScale: { min: 0, max: 50 },
      areaOfInterest: {
        min_x: -20,
        max_x: 20,
        min_y: -20,
        max_y: 20,
      },
      resolution: 1,
      pointSize: 3,
      pointRenderMode: 'robust',
      colorScale: 'jet' as ColorScale,
      targetConfig: {
        width: 1.0,
        height: 1.8,
        depth: 0.5,
        shape: 'cuboid',
        groundOffset: 0.1
      },
      cameraMode: '3D',
    };
    const nextSensors = project?.sensors || [];
    const nextSensorIds = new Set(nextSensors.map((sensor) => sensor.id));
    const alignmentSessionUpdates: Partial<SceneState> = currentState.isAlignmentMode
      ? {
        alignmentConnectivity: null,
        alignmentNavigationHistory: [],
        alignmentOriginSensorId: null,
      }
      : {};

    return set({
      currentProject: project,
      sensors: nextSensors,
      obstacles: project?.obstacles || [],
      referenceImageConfig: project?.reference_image_config || initialReferenceImageConfig,
      sceneOptions: newSceneOptions,
      selectedObjectId: null, // Deselect any object when changing projects
      shouldSimulate: !!project,
      heatmapData: null,
      heatmapTaskId: null,
      heatmapVisible: false, // Will be set to true if cache is loaded
      pointCloudVisible: true,
      sensorPoseHistory: [],
      sensorPoseRedoHistory: [],
      ...alignmentSessionUpdates,
    });
  },
  updateProjectReferenceImage: (imageUrl: string, originalFilename?: string) =>
    set((state) => ({
      currentProject: state.currentProject
        ? {
          ...state.currentProject,
          reference_image_url: imageUrl,
          reference_image_original_filename: originalFilename ?? state.currentProject.reference_image_original_filename,
        }
        : state.currentProject,
    })),
  referenceImageConfig: initialReferenceImageConfig,
  setReferenceImageConfig: (config: Partial<ReferenceImageConfig>) =>
    set((state) => {
      const newConfig = { ...state.referenceImageConfig, ...config };
      return {
        referenceImageConfig: newConfig,
        currentProject: state.currentProject
          ? { ...state.currentProject, reference_image_config: newConfig }
          : null,
      };
    }),
  resetReferenceImageConfig: () => set((state) => ({
    referenceImageConfig: initialReferenceImageConfig,
    currentProject: state.currentProject
      ? { ...state.currentProject, reference_image_config: initialReferenceImageConfig }
      : null,
  })),
  sceneOptions: {
    gridCellSize: 1,
    heatmapScale: { min: 0, max: 50 },
    areaOfInterest: {
      min_x: -20,
      max_x: 20,
      min_y: -20,
      max_y: 20,
    },
    resolution: 1,
    pointSize: 3,
    pointRenderMode: 'robust',
    colorScale: 'jet',
    targetConfig: {
      width: 1.0,
      height: 1.8,
      depth: 0.5,
      shape: 'cuboid',
      groundOffset: 0.1
    },
    cameraMode: '3D',
  },
  setSceneOptions: (options) => set((state) => {
    const newSceneOptions = typeof options === 'function' ? { ...state.sceneOptions, ...options(state.sceneOptions) } : { ...state.sceneOptions, ...options };


    if (state.heatmapTaskId) {
      const oldOpts = state.sceneOptions;
      const newOpts = newSceneOptions;

      // Helper to check if objects are different
      const isDiff = (a: any, b: any) => JSON.stringify(a) !== JSON.stringify(b);

      if (
        isDiff(oldOpts.areaOfInterest, newOpts.areaOfInterest) ||
        isDiff(oldOpts.targetConfig, newOpts.targetConfig) ||
        oldOpts.resolution !== newOpts.resolution ||
        oldOpts.gridCellSize !== newOpts.gridCellSize
      ) {
        state.cancelHeatmapGeneration();

        return {
          sceneOptions: newSceneOptions,
          currentProject: state.currentProject
            ? { ...state.currentProject, simulation_options: newSceneOptions }
            : null,
          heatmapData: null,
          heatmapVisible: false,
        };
      }
    } else {
      // Even if no task is running, invalidating changes should clear existing data
      const oldOpts = state.sceneOptions;
      const newOpts = newSceneOptions;
      const isDiff = (a: any, b: any) => JSON.stringify(a) !== JSON.stringify(b);

      if (
        isDiff(oldOpts.areaOfInterest, newOpts.areaOfInterest) ||
        isDiff(oldOpts.targetConfig, newOpts.targetConfig) ||
        oldOpts.resolution !== newOpts.resolution ||
        oldOpts.gridCellSize !== newOpts.gridCellSize
      ) {
        return {
          sceneOptions: newSceneOptions,
          currentProject: state.currentProject
            ? { ...state.currentProject, simulation_options: newSceneOptions }
            : null,
          heatmapData: null,
          heatmapVisible: false,
        };
      }
    }

    return {
      sceneOptions: newSceneOptions,
      currentProject: state.currentProject
        ? { ...state.currentProject, simulation_options: newSceneOptions }
        : null,
    };
  }),

  selectedObjectId: null,
  setSelectedObjectId: (id: string | null) => set({ selectedObjectId: id }),
  isReferenceImageAlignMode: false,
  setIsReferenceImageAlignMode: (isMode: boolean) => set((state) => {
    if (isMode && state.heatmapVisible) {
      return { isReferenceImageAlignMode: isMode, heatmapVisible: false };
    }
    return { isReferenceImageAlignMode: isMode };
  }),
  referenceImageAlignMethod: 'interactive',
  setReferenceImageAlignMethod: (method: 'interactive' | 'point-picking') => set({ referenceImageAlignMethod: method }),
  referenceImageAlignMode: null,
  setReferenceImageAlignMode: (mode: 'translate' | 'rotate' | 'scale' | null) => set({ referenceImageAlignMode: mode }),
  referenceImageScaleSensors: false,
  setReferenceImageScaleSensors: (scale: boolean) => set({ referenceImageScaleSensors: scale }),
  referenceImageScaleObstacles: false,
  setReferenceImageScaleObstacles: (scale: boolean) => set({ referenceImageScaleObstacles: scale }),
  applyReferenceImageScaleToScene: (
    baselineSensors,
    baselineObstacles,
    pivotStart,
    pivotEnd,
    scaleFactor,
    options,
  ) =>
    set((state) => {
      if (!options.scaleSensors && !options.scaleObstacles) {
        return state;
      }

      const transform: ImageDragTransform = { pivotStart, pivotEnd, scaleFactor };
      const sensors = options.scaleSensors
        ? scaleSensorsWithImageDrag(baselineSensors, transform)
        : state.sensors;
      const obstacles = options.scaleObstacles
        ? scaleObstaclesWithImageDrag(baselineObstacles, transform)
        : state.obstacles;

      if (state.heatmapTaskId) {
        state.cancelHeatmapGeneration();
      }

      return {
        sensors,
        obstacles,
        currentProject: state.currentProject
          ? { ...state.currentProject, sensors, obstacles, simulation_options: state.sceneOptions }
          : null,
        shouldSimulate: true,
        heatmapData: null,
        heatmapVisible: false,
      };
    }),
  pointPickingStep: null,
  setPointPickingStep: (step: 'origin' | 'xaxis' | 'scale-1' | 'scale-2' | 'scale-distance' | null) => set({ pointPickingStep: step }),
  pickingState: { origin: null, xAxisEnd: null, scalePointA: null, scalePointB: null },
  setPickingState: (state) => set((prev) => ({ pickingState: { ...prev.pickingState, ...state } })),
  selectedSensorType: 'Dome',
  setSelectedSensorType: (type: string) => set((state) => {
    // If switching between advanced types, reset defaults to appropriate values
    if (type === 'Rotating' && state.selectedSensorType !== 'Rotating') {
      return { 
        selectedSensorType: type, 
        defaultSensorParameters: { minVerticalAngle: -15, maxVerticalAngle: 15, verticalChannels: 16, horizontalResolution: 2000, horizontalFov: 360, effectiveRange: 100 } 
      };
    } else if (type === 'MEMS' && state.selectedSensorType !== 'MEMS') {
      return { 
        selectedSensorType: type, 
        defaultSensorParameters: { minVerticalAngle: -12.5, maxVerticalAngle: 12.5, verticalChannels: 250, horizontalResolution: 1280, horizontalFov: 120, effectiveRange: 250 } 
      };
    }
    return { selectedSensorType: type };
  }),
  useOrthographic: false,
  setUseOrthographic: (use: boolean) => set({ useOrthographic: use }),
  orthographicZoom: null,
  setOrthographicZoom: (zoom: number) => set({ orthographicZoom: zoom }),
  defaultSensorHeight: 3,
  defaultSensorRotationRate: 10,
  defaultSensorParameters: {
    minVerticalAngle: -15,
    maxVerticalAngle: 15,
    verticalChannels: 16,
    horizontalResolution: 2000,
    horizontalFov: 360,
    effectiveRange: 100
  },
  setDefaultSensorHeight: (height: number) => set({ defaultSensorHeight: height }),
  setDefaultSensorRotationRate: (rate: number) => set({ defaultSensorRotationRate: rate }),
  setDefaultSensorParameters: (params: Partial<SensorParameters>) => set((state) => ({
    defaultSensorParameters: { ...state.defaultSensorParameters, ...params }
  })),
  lastAddedSensorId: null,
  setLastAddedSensorId: (id: string | null) => set({ lastAddedSensorId: id }),
  updateSensor: (id: string, props: Partial<Sensor>) => {
    const existingSensor = get().sensors.find((sensor) => sensor.id === id);
    if (!existingSensor) {
      return;
    }

    const updatedSensor: Sensor = {
      ...existingSensor,
      ...props,
      ...alignmentInvalidationForPoseProps(existingSensor, props),
    };
    if (updatedSensor.position[2] < 0) {
      updatedSensor.position = [updatedSensor.position[0], updatedSensor.position[1], 0];
    }

    const simulationInvalidated = sensorSimulationInputsChanged(existingSensor, updatedSensor);
    if (simulationInvalidated) {
      cancelHeatmapTaskIfRunning(get);
    }

    set((state) => {
      const newSensors = state.sensors.map((sensor) => (
        sensor.id === id ? updatedSensor : sensor
      ));
      return {
        sensors: newSensors,
        currentProject: state.currentProject
          ? { ...state.currentProject, sensors: newSensors, simulation_options: state.sceneOptions }
          : null,
        ...(simulationInvalidated ? simulationInvalidationState() : {}),
      };
    });
  },
  updateObstacleProperties: (id: string, props: Partial<Obstacle>) => {
    const currentState = get();
    if (currentState.heatmapTaskId) {
      void currentState.cancelHeatmapGeneration();
    }
    set((state) => {
      const newObstacles = state.obstacles.map((obstacle) => {
        if (obstacle.id === id) {
          const updatedObstacle = { ...obstacle, ...props };
          // Apply ground constraint: bottom of obstacle must be >= 0
          if (updatedObstacle.position && updatedObstacle.size) {
            const bottomZ = updatedObstacle.position[2] - updatedObstacle.size[2] / 2;
            if (bottomZ < 0) {
              updatedObstacle.position = [
                updatedObstacle.position[0],
                updatedObstacle.position[1],
                updatedObstacle.size[2] / 2
              ];
            }
          }
          return updatedObstacle;
        }
        return obstacle;
      });
      return {
        obstacles: newObstacles,
        currentProject: state.currentProject
          ? { ...state.currentProject, obstacles: newObstacles, simulation_options: state.sceneOptions }
          : null,
        shouldSimulate: true,
        heatmapData: null,
        heatmapVisible: false,
      };
    });
  },
  addObstacle: (position: [number, number, number] = [0, 0, 0.5]) =>
    set((state) => {
      if (state.heatmapTaskId) {
        state.cancelHeatmapGeneration();
      }
      const newObstacle: Obstacle = { id: uuidv4(), position, size: [1, 1, 1], rotation: [0, 0, 0] };
      const newObstacles = [
        ...state.obstacles,
        newObstacle,
      ];
      return {
        obstacles: newObstacles,
        currentProject: state.currentProject ? { ...state.currentProject, obstacles: newObstacles, simulation_options: state.sceneOptions } : null,
        shouldSimulate: true,
        selectedObjectId: newObstacle.id,
        heatmapData: null,
        heatmapVisible: false,
      };
    }),
  addSensor: (type: string, position2d: [number, number] = [0, 0]) =>
    set((state) => {
      if (state.heatmapTaskId) {
        state.cancelHeatmapGeneration();
      }
      let parameters;
      if (type === 'Rotating' || type === 'MEMS') {
        if (state.selectedSensorType === type) {
          parameters = { ...state.defaultSensorParameters };
        } else if (type === 'MEMS') {
          parameters = {
            minVerticalAngle: -12.5,
            maxVerticalAngle: 12.5,
            verticalChannels: 250,
            horizontalResolution: 1280,
            horizontalFov: 120,
            effectiveRange: 250,
          };
        } else {
          parameters = {
            minVerticalAngle: -15,
            maxVerticalAngle: 15,
            verticalChannels: 16,
            horizontalResolution: 2000,
            horizontalFov: 360,
            effectiveRange: 100,
          };
        }
      }

      const newSensor: Sensor = {
        id: uuidv4(),
        name: 'New Sensor',
        type: type,
        rotationRate: state.defaultSensorRotationRate,
        position: [position2d[0], position2d[1], state.defaultSensorHeight],
        rotation: [0, 0, 0],
        parameters: parameters,
      };
      const newSensors = [
        ...state.sensors,
        newSensor,
      ];
      return {
        sensors: newSensors,
        currentProject: state.currentProject ? { ...state.currentProject, sensors: newSensors, simulation_options: state.sceneOptions } : null,
        shouldSimulate: true,
        selectedObjectId: newSensor.id,
        lastAddedSensorId: newSensor.id,
        heatmapData: null,
        heatmapVisible: false,
      };
    }),
  deleteSensor: (id: string) => {
    const currentState = get();
    if (currentState.heatmapTaskId) {
      void currentState.cancelHeatmapGeneration();
    }
    set((state) => {
      const newSensors = state.sensors.filter((sensor) => sensor.id !== id);
      return {
        sensors: newSensors,
        currentProject: state.currentProject
          ? { ...state.currentProject, sensors: newSensors, simulation_options: state.sceneOptions }
          : null,
        selectedObjectId: state.selectedObjectId === id ? null : state.selectedObjectId,
        shouldSimulate: true,
        heatmapData: null,
        heatmapVisible: false,
      };
    });
  },
  deleteObstacle: (id: string) => {
    const currentState = get();
    if (currentState.heatmapTaskId) {
      void currentState.cancelHeatmapGeneration();
    }
    set((state) => {
      const newObstacles = state.obstacles.filter((obstacle) => obstacle.id !== id);
      return {
        obstacles: newObstacles,
        currentProject: state.currentProject
          ? { ...state.currentProject, obstacles: newObstacles, simulation_options: state.sceneOptions }
          : null,
        selectedObjectId: state.selectedObjectId === id ? null : state.selectedObjectId,
        shouldSimulate: true,
        heatmapData: null,
        heatmapVisible: false,
      };
    });
  },
  // Wall drawing state
  isWallDrawingMode: false,
  setIsWallDrawingMode: (isWallDrawingMode: boolean) => set((state) => {
    const updates: Partial<SceneState> = {
      isWallDrawingMode,
      isAlignmentMode: isWallDrawingMode ? false : state.isAlignmentMode,
    };
    // Auto-hide heatmap when entering wall drawing mode
    if (isWallDrawingMode && state.heatmapVisible) {
      updates.heatmapVisible = false;
    }
    return updates;
  }),
  // Sensor alignment state
  isAlignmentMode: false,
  setIsAlignmentMode: (isAlignmentMode: boolean) => {
    const enteringState = get();
    if (isAlignmentMode && enteringState.heatmapTaskId) {
      void enteringState.cancelHeatmapGeneration();
    }
    set((state) => {
      const updates: Partial<SceneState> = {
        isAlignmentMode,
        isWallDrawingMode: isAlignmentMode ? false : state.isWallDrawingMode,
      };
      if (isAlignmentMode) {
        if (state.heatmapVisible) {
          updates.heatmapVisible = false;
        }
        updates.preAlignmentPointCloudVisible = state.pointCloudVisible;
        updates.alignmentPointCloudUserToggled = false;
        // In alignment mode pointCloudVisible controls aligned scan clouds (sim PC is disabled).
        updates.pointCloudVisible = true;
        updates.alignmentNavigationHistory = [];
      } else {
        if (
          state.preAlignmentPointCloudVisible !== null
          && !state.alignmentPointCloudUserToggled
        ) {
          updates.pointCloudVisible = state.preAlignmentPointCloudVisible;
        }
        updates.preAlignmentPointCloudVisible = null;
        updates.alignmentPointCloudUserToggled = false;
        updates.alignmentConnectivity = null;
        updates.alignmentOriginSensorId = null;
        updates.alignmentNavigationHistory = [];
        updates.alignmentHeightMin = null;
        updates.alignmentHeightMax = null;
      }
      return updates;
    });
  },
  alignmentOriginSensorId: null,
  alignmentConnectivity: null,
  alignmentNavigationHistory: [],
  sensorPoseHistory: [],
  sensorPoseRedoHistory: [],
  alignmentHeightMin: null,
  alignmentHeightMax: null,
  setAlignmentHeightMin: (alignmentHeightMin: number | null) => set({ alignmentHeightMin }),
  setAlignmentHeightMax: (alignmentHeightMax: number | null) => set({ alignmentHeightMax }),
  pushSensorPoseHistory: (sensorId: string) => {
    const state = get();
    const sensor = state.sensors.find((item) => item.id === sensorId);
    if (!sensor) {
      return;
    }
    const snapshot: SensorPoseSnapshot = snapshotSensorPose(sensor);
    const lastSnapshot = state.sensorPoseHistory[state.sensorPoseHistory.length - 1];
    if (
      lastSnapshot?.sensorId === snapshot.sensorId
      && sameVector3(lastSnapshot.position, snapshot.position)
      && sameVector3(lastSnapshot.rotation, snapshot.rotation)
      && lastSnapshot.floorLocked === snapshot.floorLocked
      && lastSnapshot.alignmentStatus === snapshot.alignmentStatus
    ) {
      return;
    }
    set({
      sensorPoseHistory: [
        ...state.sensorPoseHistory,
        snapshot,
      ].slice(-MAX_SENSOR_POSE_HISTORY),
      sensorPoseRedoHistory: [],
    });
  },
  undoSensorPose: () => {
    const state = get();
    if (state.sensorPoseHistory.length === 0) {
      return;
    }
    const history = [...state.sensorPoseHistory];
    const snapshot = history.pop();
    if (!snapshot) {
      return;
    }
    const sensor = state.sensors.find((item) => item.id === snapshot.sensorId);
    if (!sensor) {
      set({ sensorPoseHistory: history });
      return;
    }
    const redoSnapshot = snapshotSensorPose(sensor);
    const newSensors = applyPoseSnapshot(state.sensors, snapshot);
    const updatedSensor = newSensors.find((item) => item.id === snapshot.sensorId);
    const simulationInvalidated = Boolean(
      updatedSensor && sensorSimulationInputsChanged(sensor, updatedSensor)
    );
    if (simulationInvalidated) {
      cancelHeatmapTaskIfRunning(get);
    }
    set({
      sensors: newSensors,
      currentProject: state.currentProject
        ? { ...state.currentProject, sensors: newSensors, simulation_options: state.sceneOptions }
        : null,
      sensorPoseHistory: history,
      sensorPoseRedoHistory: [
        ...state.sensorPoseRedoHistory,
        redoSnapshot,
      ].slice(-MAX_SENSOR_POSE_HISTORY),
      selectedObjectId: snapshot.sensorId,
      ...(simulationInvalidated ? simulationInvalidationState() : {}),
    });
  },
  redoSensorPose: () => {
    const state = get();
    if (state.sensorPoseRedoHistory.length === 0) {
      return;
    }
    const redoHistory = [...state.sensorPoseRedoHistory];
    const snapshot = redoHistory.pop();
    if (!snapshot) {
      return;
    }
    const sensor = state.sensors.find((item) => item.id === snapshot.sensorId);
    if (!sensor) {
      set({ sensorPoseRedoHistory: redoHistory });
      return;
    }
    const undoSnapshot = snapshotSensorPose(sensor);
    const newSensors = applyPoseSnapshot(state.sensors, snapshot);
    const updatedSensor = newSensors.find((item) => item.id === snapshot.sensorId);
    const simulationInvalidated = Boolean(
      updatedSensor && sensorSimulationInputsChanged(sensor, updatedSensor)
    );
    if (simulationInvalidated) {
      cancelHeatmapTaskIfRunning(get);
    }
    set({
      sensors: newSensors,
      currentProject: state.currentProject
        ? { ...state.currentProject, sensors: newSensors, simulation_options: state.sceneOptions }
        : null,
      sensorPoseHistory: [
        ...state.sensorPoseHistory,
        undoSnapshot,
      ].slice(-MAX_SENSOR_POSE_HISTORY),
      sensorPoseRedoHistory: redoHistory,
      selectedObjectId: snapshot.sensorId,
      ...(simulationInvalidated ? simulationInvalidationState() : {}),
    });
  },
  commitSensorAlignment: (id: string) => {
    get().pushSensorPoseHistory(id);
    set((state) => {
      const newSensors = state.sensors.map((sensor) => {
        if (sensor.id !== id) {
          return sensor;
        }
        return {
          ...sensor,
          alignmentStatus: 'aligned' as const,
          alignedPosition: [...sensor.position] as [number, number, number],
          alignedRotation: [...sensor.rotation] as [number, number, number],
        };
      });
      const history = state.selectedObjectId
        ? [...state.alignmentNavigationHistory, state.selectedObjectId]
        : state.alignmentNavigationHistory;
      return {
        sensors: newSensors,
        currentProject: state.currentProject
          ? { ...state.currentProject, sensors: newSensors, simulation_options: state.sceneOptions }
          : null,
        alignmentNavigationHistory: history,
      };
    });
    void get().refreshAlignmentConnectivity(true);
  },
  resetSensorToDesign: (id: string) => {
    get().pushSensorPoseHistory(id);
    const state = get();
    const sensor = state.sensors.find((item) => item.id === id);
    if (!sensor?.designPosition || !sensor.designRotation) {
      return;
    }

    const updatedSensor: Sensor = {
      ...sensor,
      position: [...sensor.designPosition] as [number, number, number],
      rotation: [...sensor.designRotation] as [number, number, number],
      alignmentStatus: 'unaligned',
    };
    const simulationInvalidated = sensorSimulationInputsChanged(sensor, updatedSensor);
    if (simulationInvalidated) {
      cancelHeatmapTaskIfRunning(get);
    }

    set((currentState) => {
      const newSensors = currentState.sensors.map((item) => (
        item.id === id ? updatedSensor : item
      ));
      return {
        sensors: newSensors,
        currentProject: currentState.currentProject
          ? { ...currentState.currentProject, sensors: newSensors, simulation_options: currentState.sceneOptions }
          : null,
        ...(simulationInvalidated ? simulationInvalidationState() : {}),
      };
    });
  },
  restoreSensorToAligned: (id: string) => {
    const state = get();
    const sensor = state.sensors.find((item) => item.id === id);
    if (!sensor?.alignedPosition || !sensor.alignedRotation) {
      return;
    }

    get().pushSensorPoseHistory(id);

    const updatedSensor: Sensor = {
      ...sensor,
      position: [...sensor.alignedPosition] as [number, number, number],
      rotation: [...sensor.alignedRotation] as [number, number, number],
      alignmentStatus: 'aligned',
    };
    const simulationInvalidated = sensorSimulationInputsChanged(sensor, updatedSensor);
    if (simulationInvalidated) {
      cancelHeatmapTaskIfRunning(get);
    }

    set((currentState) => {
      const newSensors = currentState.sensors.map((item) => (
        item.id === id ? updatedSensor : item
      ));
      return {
        sensors: newSensors,
        currentProject: currentState.currentProject
          ? { ...currentState.currentProject, sensors: newSensors, simulation_options: currentState.sceneOptions }
          : null,
        ...(simulationInvalidated ? simulationInvalidationState() : {}),
      };
    });
  },
  advanceAlignmentSensor: (direction: 'next' | 'prev') => {
    const state = get();

    if (direction === 'prev') {
      const history = [...state.alignmentNavigationHistory];
      const previousId = history.pop();
      if (!previousId) {
        return;
      }
      set({ selectedObjectId: previousId, alignmentNavigationHistory: history });
      void get().refreshAlignmentConnectivity();
      return;
    }

    const placed_sensor_ids = state.sensors
      .filter((sensor) => sensor.pcdUrl && sensor.alignmentStatus === 'aligned')
      .map((sensor) => sensor.id);

    if (!state.selectedObjectId) {
      const connectivity = computeAlignmentConnectivityLocal({
        sensors: state.sensors,
        obstacles: state.obstacles,
        originSensorId: null,
        placedSensorIds: placed_sensor_ids,
        activeSensorId: null,
        excludedSensorIds: state.alignmentNavigationHistory,
      });

      const targetId =
        placed_sensor_ids.length === 0
          ? connectivity.origin_sensor_id
          : nextAlignmentCandidateId(connectivity);

      if (!targetId) {
        return;
      }

      set({
        selectedObjectId: targetId,
        alignmentOriginSensorId: connectivity.origin_sensor_id,
        alignmentNavigationHistory: [],
      });
      void get().refreshAlignmentConnectivity();
      return;
    }

    const connectivity = computeAlignmentConnectivityLocal({
      sensors: state.sensors,
      obstacles: state.obstacles,
      originSensorId: null,
      placedSensorIds: placed_sensor_ids,
      activeSensorId: state.selectedObjectId,
      excludedSensorIds: state.alignmentNavigationHistory,
    });

    const nextId = nextAlignmentCandidateId(connectivity);
    if (!nextId || nextId === state.selectedObjectId) {
      return;
    }

    const history = state.selectedObjectId
      ? [...state.alignmentNavigationHistory, state.selectedObjectId]
      : state.alignmentNavigationHistory;

    set({ selectedObjectId: nextId, alignmentNavigationHistory: history });
    void get().refreshAlignmentConnectivity();
  },
  autoFloorSensor: async (id: string) => {
    const state = get();
    if (!state.currentProject || !state.isAlignmentMode) {
      return { success: false, message: 'Alignment mode is not active.' };
    }

    try {
      const sensor = state.sensors.find((item) => item.id === id);
      if (!sensor?.pcdUrl) {
        return { success: false, message: 'Sensor has no PCD data.' };
      }

      const result = await autoFloorSensorApi(state.currentProject.id, {
        sensor_id: id,
        position: sensor.position,
        rotation: sensor.rotation,
        lock_floor: true,
      });
      if (!result.success || !result.position || !result.rotation) {
        return {
          success: false,
          message: result.message || 'Could not detect a flat floor plane in this scan.',
        };
      }

      const updates: Partial<Sensor> = {
        position: result.position,
        rotation: result.rotation,
        floorLocked: true,
        alignmentStatus: 'unaligned',
      };
      get().pushSensorPoseHistory(id);
      get().updateSensor(id, updates);
      return { success: true, message: result.message || 'Floor aligned from scan plane.' };
    } catch (error) {
      console.warn('Auto-floor detection failed', error);
      return { success: false, message: 'Auto-floor request failed.' };
    }
  },
  runIcpSensor: async (id: string) => {
    const state = get();
    if (!state.currentProject || !state.isAlignmentMode) {
      return { success: false, message: 'Alignment mode is not active.' };
    }

    try {
      const sensor = state.sensors.find((item) => item.id === id);
      if (!sensor?.pcdUrl) {
        return { success: false, message: 'Sensor has no PCD data.' };
      }

      const result = await runIcpAlignmentApi(state.currentProject.id, {
        sensor_id: id,
        position: sensor.position,
        rotation: sensor.rotation,
      });
      if (!result.success || !result.position || !result.rotation) {
        return {
          success: false,
          message: result.message || 'ICP could not find enough correspondences.',
        };
      }

      const updates: Partial<Sensor> = {
        position: result.position,
        rotation: result.rotation,
        alignmentStatus: 'unaligned',
      };
      get().pushSensorPoseHistory(id);
      get().updateSensor(id, updates);
      return {
        success: true,
        message: result.message,
      };
    } catch (error) {
      console.warn('ICP alignment failed', error);
      return { success: false, message: 'ICP alignment request failed.' };
    }
  },
  refreshAlignmentConnectivity: async (selectRecommendedNext = false) => {
    const state = get();
    if (!state.currentProject || !state.isAlignmentMode) {
      return;
    }

    const requestSeq = ++alignmentConnectivityRequestSeq;

    const placed_sensor_ids = state.sensors
      .filter((sensor) => sensor.pcdUrl && sensor.alignmentStatus === 'aligned')
      .map((sensor) => sensor.id);

    const request = {
      origin_sensor_id: null,
      placed_sensor_ids,
      active_sensor_id: state.selectedObjectId,
      excluded_sensor_ids: state.alignmentNavigationHistory,
      require_los: true,
    };

    const applyConnectivityResult = (updates: Partial<SceneState>) => {
      if (!get().isAlignmentMode || requestSeq !== alignmentConnectivityRequestSeq) {
        return;
      }
      set(updates);
    };

    try {
      const result = await fetchAlignmentConnectivity(state.currentProject.id, request);
      if (!result) {
        const fallback = computeAlignmentConnectivityLocal({
          sensors: state.sensors,
          obstacles: state.obstacles,
          originSensorId: null,
          placedSensorIds: placed_sensor_ids,
          activeSensorId: state.selectedObjectId,
          excludedSensorIds: state.alignmentNavigationHistory,
        });
        const updates: Partial<typeof state> = {
          alignmentConnectivity: fallback,
          alignmentOriginSensorId: fallback.origin_sensor_id,
        };
        if (selectRecommendedNext && fallback.recommended_next?.sensor_id) {
          updates.selectedObjectId = fallback.recommended_next.sensor_id;
        }
        applyConnectivityResult(updates);
        return;
      }
      const updates: Partial<typeof state> = {
        alignmentConnectivity: result,
        alignmentOriginSensorId: result.origin_sensor_id,
      };
      if (selectRecommendedNext && result.recommended_next?.sensor_id) {
        updates.selectedObjectId = result.recommended_next.sensor_id;
      }
      applyConnectivityResult(updates);
    } catch (error) {
      console.warn('Alignment connectivity API failed, using client fallback', error);
      const fallback = computeAlignmentConnectivityLocal({
        sensors: state.sensors,
        obstacles: state.obstacles,
        originSensorId: null,
        placedSensorIds: placed_sensor_ids,
        activeSensorId: state.selectedObjectId,
        excludedSensorIds: state.alignmentNavigationHistory,
      });
      const updates: Partial<typeof state> = {
        alignmentConnectivity: fallback,
        alignmentOriginSensorId: fallback.origin_sensor_id,
      };
      if (selectRecommendedNext && fallback.recommended_next?.sensor_id) {
        updates.selectedObjectId = fallback.recommended_next.sensor_id;
      }
      applyConnectivityResult(updates);
    }
  },
  currentWallPoints: [],
  setCurrentWallPoints: (currentWallPoints: [number, number][]) => set({ currentWallPoints }),
  wallThickness: 0.2,
  setWallThickness: (wallThickness: number) => set({ wallThickness }),
  wallHeight: 2.5,
  setWallHeight: (wallHeight: number) => set({ wallHeight }),
  finishCurrentWall: () =>
    set((state) => {
      console.log('finishCurrentWall called with', state.currentWallPoints.length, 'points');

      if (state.currentWallPoints.length < 2) {
        console.log('Not enough points to create wall (need at least 2)');
        return state; // Need at least 2 points for a wall
      }

      // Convert wall points to obstacles
      const newObstacles = [...state.obstacles];

      if (state.heatmapTaskId) {
        state.cancelHeatmapGeneration();
      }

      for (let i = 0; i < state.currentWallPoints.length - 1; i++) {
        const start = state.currentWallPoints[i];
        const end = state.currentWallPoints[i + 1];

        // Calculate center point
        const centerX = (start[0] + end[0]) / 2;
        const centerY = (start[1] + end[1]) / 2;

        // Calculate length
        const length = Math.sqrt((end[0] - start[0]) ** 2 + (end[1] - start[1]) ** 2);

        // Calculate rotation
        const angle = Math.atan2(end[1] - start[1], end[0] - start[0]);

        const obstacle: Obstacle = {
          id: `wall_${Date.now()}_segment_${i + 1}`,
          position: [centerX, centerY, state.wallHeight / 2],
          size: [length, state.wallThickness, state.wallHeight],
          rotation: [0, 0, angle]
        };

        console.log('Creating obstacle:', obstacle);
        newObstacles.push(obstacle);
      }

      console.log('Created', newObstacles.length - state.obstacles.length, 'new obstacles');

      return {
        obstacles: newObstacles,
        currentProject: state.currentProject
          ? { ...state.currentProject, obstacles: newObstacles, simulation_options: state.sceneOptions }
          : null,
        currentWallPoints: [],
        shouldSimulate: true,
        heatmapData: null,
        heatmapVisible: false,
      };
    }),
  clearCurrentWall: () => set({ currentWallPoints: [] }),
  sceneCanvas: null,
  sceneCamera: null,
  setSceneRefs: (canvas, camera) => set({ sceneCanvas: canvas, sceneCamera: camera }),
}));

// Add a selector for convenience
export const selectSensors = (state: SceneState) => state.sensors;